import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpIcon, ArrowDownIcon, PhotoIcon, XMarkIcon } from '@heroicons/react/24/outline';
import {
  eventSponsorsApi,
  sponsorsApi,
  EventSponsor,
  EventSponsorUpdate,
  Sponsor,
  SponsorTier,
  sponsorLogoSrc,
} from '../../api/client';
import { getErrorMessage } from '../../utils/errorHandler';

interface SponsorManagerProps {
  eventId: number;
  eventTitle: string;
  onClose: () => void;
}

const TIER_LABELS: Record<SponsorTier, string> = {
  headline: 'Headline',
  partner: 'Partner',
  supporter: 'Supporter',
};

const TIER_RANK: Record<SponsorTier, number> = { headline: 0, partner: 1, supporter: 2 };

const SURFACES: { key: keyof Pick<EventSponsor, 'show_on_event_page' | 'show_on_badge' | 'show_in_emails' | 'show_on_cards'>; label: string }[] = [
  { key: 'show_on_event_page', label: 'Event page' },
  { key: 'show_on_badge', label: 'Badges' },
  { key: 'show_in_emails', label: 'Emails' },
  { key: 'show_on_cards', label: 'Social cards' },
];

const sortForDisplay = (list: EventSponsor[]): EventSponsor[] =>
  [...list].sort((a, b) => TIER_RANK[a.tier] - TIER_RANK[b.tier] || a.sort_order - b.sort_order);

const SponsorManager: React.FC<SponsorManagerProps> = ({ eventId, eventTitle, onClose }) => {
  const [attached, setAttached] = useState<EventSponsor[]>([]);
  const [library, setLibrary] = useState<Sponsor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [selectedSponsorId, setSelectedSponsorId] = useState<string>('');

  useEffect(() => {
    const load = async () => {
      try {
        const [eventSponsors, allSponsors] = await Promise.all([
          eventSponsorsApi.list(eventId),
          sponsorsApi.listSponsors(),
        ]);
        setAttached(sortForDisplay(eventSponsors));
        setLibrary(allSponsors);
        setError(null);
      } catch (err) {
        setError(getErrorMessage(err));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [eventId]);

  const attachedIds = new Set(attached.map((s) => s.sponsor_id));
  const available = library.filter((s) => s.is_active && !attachedIds.has(s.id));

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const handleAdd = () =>
    run(async () => {
      const sponsorId = parseInt(selectedSponsorId, 10);
      if (!sponsorId) return;
      const created = await eventSponsorsApi.attach(eventId, { sponsor_id: sponsorId });
      setAttached((prev) => sortForDisplay([...prev, created]));
      setSelectedSponsorId('');
    });

  const handleUpdate = (sponsorId: number, data: EventSponsorUpdate) =>
    run(async () => {
      const updated = await eventSponsorsApi.update(eventId, sponsorId, data);
      setAttached((prev) => sortForDisplay(prev.map((s) => (s.sponsor_id === sponsorId ? updated : s))));
    });

  const handleRemove = (sponsor: EventSponsor) =>
    run(async () => {
      await eventSponsorsApi.detach(eventId, sponsor.sponsor_id);
      setAttached((prev) => prev.filter((s) => s.sponsor_id !== sponsor.sponsor_id));
    });

  const handleMove = (index: number, direction: -1 | 1) =>
    run(async () => {
      const target = index + direction;
      if (target < 0 || target >= attached.length) return;
      const next = [...attached];
      [next[index], next[target]] = [next[target], next[index]];
      const renumbered = next.map((s, i) => ({ ...s, sort_order: i }));
      await eventSponsorsApi.reorder(eventId, renumbered.map((s) => s.sponsor_id));
      setAttached(renumbered);
    });

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-6 border-b flex justify-between items-start">
          <div>
            <h2 className="text-xl font-semibold">Sponsors</h2>
            <p className="text-sm text-gray-500 mt-1">{eventTitle}</p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">✕</button>
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <>
              {error && (
                <div className="bg-red-50 border border-red-200 rounded p-3 text-red-700 text-sm mb-4">
                  {error}
                </div>
              )}

              {attached.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-6">
                  No sponsors attached to this event yet.
                </p>
              ) : (
                <div className="space-y-2">
                  <div className="hidden sm:grid grid-cols-[2rem_3.5rem_1fr_8rem_auto_4rem] gap-3 items-center px-2 text-xs font-medium text-gray-500 uppercase tracking-wide">
                    <span></span>
                    <span></span>
                    <span>Sponsor</span>
                    <span>Tier</span>
                    <span>Show on</span>
                    <span></span>
                  </div>
                  {attached.map((sponsor, index) => (
                    <div
                      key={sponsor.sponsor_id}
                      className="grid grid-cols-1 sm:grid-cols-[2rem_3.5rem_1fr_8rem_auto_4rem] gap-3 items-center border border-gray-200 rounded-md p-2"
                    >
                      <div className="flex sm:flex-col gap-1">
                        <button
                          type="button"
                          disabled={busy || index === 0}
                          onClick={() => handleMove(index, -1)}
                          className="p-1 text-gray-500 hover:bg-gray-100 rounded disabled:opacity-30"
                          title="Move up"
                        >
                          <ArrowUpIcon className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          disabled={busy || index === attached.length - 1}
                          onClick={() => handleMove(index, 1)}
                          className="p-1 text-gray-500 hover:bg-gray-100 rounded disabled:opacity-30"
                          title="Move down"
                        >
                          <ArrowDownIcon className="h-4 w-4" />
                        </button>
                      </div>
                      <div className="h-12 w-14 bg-white border border-gray-100 rounded flex items-center justify-center p-1">
                        {sponsor.logo_url ? (
                          <img
                            src={sponsorLogoSrc(sponsor.logo_url)}
                            alt={sponsor.name}
                            className="max-h-full max-w-full object-contain"
                          />
                        ) : (
                          <PhotoIcon className="h-5 w-5 text-gray-300" />
                        )}
                      </div>
                      <div className="min-w-0">
                        <div className="font-medium text-gray-900 truncate">{sponsor.name}</div>
                        {sponsor.website_url && (
                          <div className="text-xs text-gray-500 truncate">{sponsor.website_url}</div>
                        )}
                      </div>
                      <select
                        value={sponsor.tier}
                        disabled={busy}
                        onChange={(e) => handleUpdate(sponsor.sponsor_id, { tier: e.target.value as SponsorTier })}
                        className="px-2 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                      >
                        {(Object.keys(TIER_LABELS) as SponsorTier[]).map((tier) => (
                          <option key={tier} value={tier}>{TIER_LABELS[tier]}</option>
                        ))}
                      </select>
                      <div className="flex flex-wrap gap-x-3 gap-y-1">
                        {SURFACES.map(({ key, label }) => (
                          <label key={key} className="flex items-center gap-1 text-xs text-gray-700 whitespace-nowrap">
                            <input
                              type="checkbox"
                              checked={sponsor[key]}
                              disabled={busy}
                              onChange={(e) => handleUpdate(sponsor.sponsor_id, { [key]: e.target.checked })}
                              className="h-3.5 w-3.5 text-blue-600 border-gray-300 rounded"
                            />
                            {label}
                          </label>
                        ))}
                      </div>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => handleRemove(sponsor)}
                        className="px-2 py-1 text-xs text-red-600 hover:bg-red-50 rounded flex items-center gap-1 justify-self-end"
                      >
                        <XMarkIcon className="h-4 w-4" />
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-6 pt-4 border-t border-gray-100">
                <label className="block text-sm font-medium text-gray-700 mb-2">Add sponsor</label>
                <div className="flex gap-2">
                  <select
                    value={selectedSponsorId}
                    disabled={busy || available.length === 0}
                    onChange={(e) => setSelectedSponsorId(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">
                      {available.length === 0 ? 'All sponsors in the library are already attached' : 'Choose a sponsor…'}
                    </option>
                    {available.map((s) => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={handleAdd}
                    disabled={busy || !selectedSponsorId}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium disabled:opacity-50"
                  >
                    Add
                  </button>
                </div>
                <p className="mt-2 text-xs text-gray-500">
                  Changes are saved immediately.{' '}
                  <Link to="/organizers/sponsors" className="text-blue-600 hover:text-blue-800">
                    Manage sponsor library
                  </Link>
                </p>
              </div>
            </>
          )}
        </div>

        <div className="p-4 border-t flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default SponsorManager;
